import React, { useEffect, useRef, useState } from "react";
import { Calendar, Plus, Sparkles, Upload, FileAudio, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_COLORS = {
    ready: "bg-[#dcfce7] text-[#16a34a] border-[#bbf7d0]",
    processing: "bg-blue-50 text-blue-700 border-blue-200",
    uploaded: "bg-gray-100 text-gray-700 border-gray-200",
    failed: "bg-red-50 text-red-700 border-red-200",
    pending_transcription: "bg-amber-50 text-amber-800 border-amber-200",
};

const AUDIO_EXTS = ["mp3", "mp4", "mpeg", "mpga", "m4a", "wav", "webm"];
const AUDIO_MIME = "audio/mpeg,audio/mp3,audio/mp4,audio/wav,audio/webm,audio/x-m4a,audio/mpga,video/mp4,video/webm";

export default function Meetings() {
    const [meetings, setMeetings] = useState([]);
    const [active, setActive] = useState(null);
    const [tab, setTab] = useState("summary");
    const [showNew, setShowNew] = useState(false);
    const [newMode, setNewMode] = useState("paste"); // "paste" | "audio"
    const [nTitle, setNTitle] = useState("");
    const [nTranscript, setNTranscript] = useState("");
    const [nParticipants, setNParticipants] = useState("");
    const [nFile, setNFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const fileInputRef = useRef(null);
    const pollTimerRef = useRef(null);

    const load = async () => {
        const { data } = await api.get("/meetings");
        setMeetings(data.meetings || []);
    };
    useEffect(() => {
        load();
        return () => {
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        };
    }, []);

    const open = async (id) => {
        const { data } = await api.get(`/meetings/${id}`);
        setActive(data);
        setTab("summary");
        // If the meeting is still processing (audio flow), start polling.
        setupPolling(id, data.status);
    };

    // Poll while status is "processing" so the transcription completion
    // surfaces without a manual refresh.
    const setupPolling = (id, initialStatus) => {
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        if (initialStatus !== "processing") return;
        pollTimerRef.current = setInterval(async () => {
            try {
                const { data } = await api.get(`/meetings/${id}`);
                setActive((cur) => (cur?.id === id ? data : cur));
                if (data.status !== "processing") {
                    clearInterval(pollTimerRef.current);
                    pollTimerRef.current = null;
                    await load();
                    if (data.status === "ready") toast.success("Transcription ready.");
                    else if (data.status === "failed") toast.error(data.transcription_error || "Transcription failed.");
                }
            } catch {
                /* transient error; keep polling */
            }
        }, 4000);
    };

    const create = async () => {
        if (!nTitle.trim()) return toast.error("Add a title");
        const body = {
            title: nTitle,
            date: new Date().toISOString(),
            participants: nParticipants.split(",").map((s) => s.trim()).filter(Boolean),
            transcript: nTranscript,
        };
        const { data } = await api.post("/meetings", body);
        setShowNew(false);
        resetNewForm();
        await load();
        await open(data.id);
        toast.success("Meeting created — click Process with AI to generate minutes.");
    };

    const uploadAudio = async () => {
        if (!nTitle.trim()) return toast.error("Add a title");
        if (!nFile) return toast.error("Select an audio file");
        const ext = nFile.name.split(".").pop().toLowerCase();
        if (!AUDIO_EXTS.includes(ext)) {
            return toast.error(`Unsupported format .${ext}. Try ${AUDIO_EXTS.slice(0, 4).join(", ")}, ...`);
        }
        if (nFile.size > 25 * 1024 * 1024) {
            return toast.error("File too large. Max is 25MB.");
        }

        const form = new FormData();
        form.append("file", nFile);
        form.append("title", nTitle);
        form.append("participants", nParticipants);
        form.append("date", new Date().toISOString());

        setUploading(true);
        try {
            const { data } = await api.post("/meetings/upload-audio", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setShowNew(false);
            resetNewForm();
            await load();
            await open(data.id);
            toast.success("Uploaded — transcribing in the background.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const resetNewForm = () => {
        setNTitle("");
        setNTranscript("");
        setNParticipants("");
        setNFile(null);
        setNewMode("paste");
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const process = async () => {
        if (!active) return;
        setProcessing(true);
        try {
            const { data } = await api.post(`/meetings/${active.id}/process`);
            setActive(data);
            await load();
            toast.success("Processed");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Processing failed");
        } finally {
            setProcessing(false);
        }
    };

    const isTranscribing = active?.status === "processing" && active?.source === "audio_upload" && !active?.transcript;

    return (
        <div className="h-full flex bg-white">
            {/* Left: list */}
            <aside className="w-[320px] flex-shrink-0 border-r border-[#e8e8e3] bg-[#fafaf7] flex flex-col overflow-y-auto wfos-scrollbar">
                <div className="p-4 flex items-center justify-between">
                    <div className="wfos-label">Meetings</div>
                    <button
                        onClick={() => setShowNew(true)}
                        data-testid="meet-new-btn"
                        className="inline-flex items-center gap-1 rounded-md bg-[#16a34a] px-2 h-7 text-xs font-medium text-white hover:bg-[#15803d]"
                    >
                        <Plus className="w-3 h-3" strokeWidth={2} /> New
                    </button>
                </div>
                <ul className="px-2 pb-6 space-y-1">
                    {meetings.map((m) => (
                        <li key={m.id}>
                            <button
                                onClick={() => open(m.id)}
                                data-testid={`meet-list-${m.id}`}
                                className={`w-full text-left rounded-md p-3 hover:bg-white ${active?.id === m.id ? "bg-white border border-[#e8e8e3]" : ""}`}
                            >
                                <div className="text-[13px] font-medium text-gray-900 truncate">{m.title}</div>
                                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-500">
                                    <span>{new Date(m.date).toLocaleDateString()}</span>
                                    <span>·</span>
                                    <span>{m.duration_minutes || "?"} min</span>
                                    <span className={`wfos-pill text-[9px] border ${STATUS_COLORS[m.status] || STATUS_COLORS.uploaded}`}>{m.status}</span>
                                </div>
                            </button>
                        </li>
                    ))}
                    {meetings.length === 0 && (
                        <li className="text-xs text-gray-400 p-4 text-center">No meetings yet.</li>
                    )}
                </ul>
            </aside>

            {/* Right: detail */}
            <main className="flex-1 flex flex-col min-w-0 overflow-y-auto wfos-scrollbar">
                {active ? (
                    <>
                        <div className="border-b border-[#e8e8e3] px-6 py-4 flex flex-wrap items-center justify-between gap-3">
                            <div className="min-w-0">
                                <div className="text-lg font-medium tracking-tight text-gray-900">{active.title}</div>
                                <div className="mt-1 text-xs text-gray-500">
                                    {new Date(active.date).toLocaleString()} · {active.duration_minutes || 0} min · {(active.participants || []).slice(0, 4).join(", ")}
                                </div>
                            </div>
                            <button
                                onClick={process}
                                disabled={processing || isTranscribing || !active.transcript}
                                data-testid="meet-process"
                                className="inline-flex items-center gap-1 rounded-md bg-[#16a34a] px-3 h-8 text-xs font-medium text-white hover:bg-[#15803d] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Sparkles className="w-3.5 h-3.5" strokeWidth={2} /> {processing ? "Processing…" : (active.summary ? "Regenerate with AI" : "Process with AI")}
                            </button>
                        </div>

                        {isTranscribing && (
                            <div
                                data-testid="meet-transcribing-banner"
                                className="border-b border-[#e8e8e3] bg-blue-50/60 px-6 py-3 flex items-center gap-2 text-[13px] text-blue-800"
                            >
                                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                                Transcribing {active.audio_filename || "audio"} via Whisper. Minutes will appear automatically.
                            </div>
                        )}
                        {active.status === "failed" && active.transcription_error && (
                            <div
                                data-testid="meet-transcribe-failed"
                                className="border-b border-[#e8e8e3] bg-red-50 px-6 py-3 text-[13px] text-red-800 flex items-center justify-between gap-3"
                            >
                                <span>Transcription failed: {active.transcription_error}</span>
                                <button
                                    onClick={() => { setShowNew(true); setNewMode("paste"); }}
                                    data-testid="meet-transcribe-fallback"
                                    className="rounded-md border border-red-200 bg-white px-2 h-7 text-[11px] font-medium text-red-800 hover:border-red-400"
                                >
                                    Paste transcript instead
                                </button>
                            </div>
                        )}

                        <div className="border-b border-[#e8e8e3] px-6 flex gap-1">
                            {[
                                { k: "summary", label: "Summary" },
                                { k: "minutes", label: "Minutes" },
                                { k: "action_items", label: "Action Items" },
                                { k: "decisions", label: "Decisions" },
                                { k: "transcript", label: "Transcript" },
                            ].map((t) => (
                                <button
                                    key={t.k}
                                    onClick={() => setTab(t.k)}
                                    data-testid={`meet-tab-${t.k}`}
                                    className={`px-3 h-9 text-xs font-medium border-b-2 ${tab === t.k ? "border-[#16a34a] text-gray-900" : "border-transparent text-gray-500 hover:text-gray-900"}`}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>

                        <div className="flex-1 px-6 py-5">
                            {tab === "summary" && (
                                <div className="wfos-card p-4 text-sm text-gray-800 leading-relaxed whitespace-pre-wrap" data-testid="meet-summary">
                                    {active.summary || <span className="text-gray-400">{isTranscribing ? "Waiting for transcription to complete…" : "No summary yet. Click \"Process with AI\"."}</span>}
                                </div>
                            )}
                            {tab === "minutes" && (
                                <ul className="wfos-card p-4 space-y-2 text-sm text-gray-800" data-testid="meet-minutes">
                                    {(active.minutes || []).length ? (
                                        active.minutes.map((m, i) => <li key={i}>• {m}</li>)
                                    ) : <li className="text-gray-400">No minutes yet.</li>}
                                </ul>
                            )}
                            {tab === "action_items" && (
                                <ul className="wfos-card p-4 divide-y divide-[#e8e8e3]" data-testid="meet-action-items">
                                    {(active.action_items || []).length ? active.action_items.map((a) => (
                                        <li key={a.id} className="py-2.5 flex items-start justify-between gap-3">
                                            <div>
                                                <div className="text-sm text-gray-900">{a.title}</div>
                                                <div className="mt-0.5 text-[11px] text-gray-500">
                                                    {a.assignee || "Unassigned"}{a.due_date ? ` · due ${new Date(a.due_date).toLocaleDateString()}` : ""}
                                                </div>
                                            </div>
                                            <span className={`wfos-pill text-[10px] ${a.status === "done" ? "wfos-pill--green" : ""}`}>{a.status || "open"}</span>
                                        </li>
                                    )) : <li className="text-sm text-gray-400 py-2">No action items yet.</li>}
                                </ul>
                            )}
                            {tab === "decisions" && (
                                <ul className="wfos-card p-4 space-y-2 text-sm text-gray-800" data-testid="meet-decisions">
                                    {(active.decisions || []).length ? active.decisions.map((d, i) => <li key={i}>✓ {d}</li>) : <li className="text-gray-400">No decisions logged.</li>}
                                </ul>
                            )}
                            {tab === "transcript" && (
                                <pre className="wfos-card p-4 text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed" data-testid="meet-transcript">
                                    {active.transcript || <span className="text-gray-400">{isTranscribing ? "Transcribing…" : "No transcript."}</span>}
                                </pre>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="flex-1 grid place-items-center text-sm text-gray-500">
                        <div className="text-center">
                            <Calendar className="w-8 h-8 mx-auto text-gray-300" strokeWidth={1.5} />
                            <div className="mt-2">Select a meeting on the left or start a new one.</div>
                        </div>
                    </div>
                )}
            </main>

            {showNew && (
                <div className="fixed inset-0 bg-black/30 grid place-items-center z-50" onClick={(e) => { if (e.target === e.currentTarget) setShowNew(false); }}>
                    <div className="wfos-card p-6 w-[640px]" data-testid="meet-new-modal">
                        <div className="text-lg font-medium text-gray-900">New meeting</div>
                        <p className="mt-1 text-xs text-gray-500">Upload audio and we'll transcribe it, or paste a transcript directly.</p>

                        {/* Mode tabs */}
                        <div className="mt-4 flex gap-1 border-b border-[#e8e8e3]">
                            {[
                                { k: "paste", label: "Paste transcript" },
                                { k: "audio", label: "Upload audio" },
                            ].map((t) => (
                                <button
                                    key={t.k}
                                    onClick={() => setNewMode(t.k)}
                                    data-testid={`meet-new-mode-${t.k}`}
                                    className={`px-3 h-9 text-xs font-medium border-b-2 -mb-px ${newMode === t.k ? "border-[#16a34a] text-gray-900" : "border-transparent text-gray-500 hover:text-gray-900"}`}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>

                        <div className="mt-4 space-y-3">
                            <input
                                value={nTitle}
                                onChange={(e) => setNTitle(e.target.value)}
                                placeholder="Title"
                                className="w-full h-9 rounded-md border border-[#e8e8e3] px-2 text-sm"
                                data-testid="meet-new-title"
                            />
                            <input
                                value={nParticipants}
                                onChange={(e) => setNParticipants(e.target.value)}
                                placeholder="Participants (comma separated)"
                                data-testid="meet-new-participants"
                                className="w-full h-9 rounded-md border border-[#e8e8e3] px-2 text-sm"
                            />

                            {newMode === "paste" ? (
                                <textarea
                                    value={nTranscript}
                                    onChange={(e) => setNTranscript(e.target.value)}
                                    placeholder="Paste transcript…"
                                    className="w-full h-56 rounded-md border border-[#e8e8e3] px-2 py-1.5 text-sm font-mono resize-none"
                                    data-testid="meet-new-transcript"
                                />
                            ) : (
                                <div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept={AUDIO_MIME}
                                        onChange={(e) => setNFile(e.target.files?.[0] || null)}
                                        className="hidden"
                                        data-testid="meet-new-audio-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        data-testid="meet-new-audio-pick"
                                        className="w-full h-32 rounded-md border-2 border-dashed border-[#e8e8e3] bg-[#fafaf7] hover:border-[#16a34a]/40 hover:bg-[#f0fdf4]/40 grid place-items-center transition-colors"
                                    >
                                        <div className="text-center">
                                            <FileAudio className="w-6 h-6 mx-auto text-[#16a34a]" strokeWidth={1.75} />
                                            <div className="mt-2 text-sm font-medium text-gray-900">
                                                {nFile ? nFile.name : "Click to select an audio file"}
                                            </div>
                                            <div className="mt-0.5 text-[11px] text-gray-500">
                                                {nFile
                                                    ? `${(nFile.size / (1024 * 1024)).toFixed(2)} MB`
                                                    : "MP3, WAV, M4A, WEBM, MP4 — up to 25 MB"}
                                            </div>
                                        </div>
                                    </button>
                                    <div className="mt-2 text-[11px] text-gray-500">
                                        Transcribed via OpenAI Whisper. Minutes and action items are generated automatically once transcription completes.
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                onClick={() => { setShowNew(false); resetNewForm(); }}
                                data-testid="meet-new-cancel"
                                className="rounded-md border border-[#e8e8e3] px-3 h-8 text-xs text-gray-700"
                            >
                                Cancel
                            </button>
                            {newMode === "paste" ? (
                                <button
                                    onClick={create}
                                    data-testid="meet-new-create"
                                    className="rounded-md bg-[#16a34a] px-3 h-8 text-xs font-medium text-white hover:bg-[#15803d]"
                                >
                                    Create
                                </button>
                            ) : (
                                <button
                                    onClick={uploadAudio}
                                    disabled={uploading || !nFile}
                                    data-testid="meet-new-upload"
                                    className="inline-flex items-center gap-1 rounded-md bg-[#16a34a] px-3 h-8 text-xs font-medium text-white hover:bg-[#15803d] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {uploading ? (
                                        <><Loader2 className="w-3 h-3 animate-spin" strokeWidth={2} /> Uploading…</>
                                    ) : (
                                        <><Upload className="w-3 h-3" strokeWidth={2} /> Upload & transcribe</>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
